import React, { useEffect, useMemo, useRef, useState } from "react";
import codevexLogo from "../Icons/codevex.png";

function MenuItem({ children, onClick, isOpen, "aria-haspopup": ariaHasPopup }) {
  return (
    <button
      type="button"
      className="mac-menuItem"
      onClick={onClick}
      aria-haspopup={ariaHasPopup}
      aria-expanded={typeof isOpen === "boolean" ? isOpen : undefined}
    >
      {children}
    </button>
  );
}

export default function MenuBar({
  onNewMemo,
  onDeleteMemo,
  onDeleteAllMemos,
  onArrangeIcons,
  snapToGrid,
  onToggleSnapToGrid,
  hasOpenMemo,
  canDeleteMemo,
  hasDeletableMemos,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    function onDocMouseDown(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpenMenu(null);
    }
    function onDocKeyDown(e) {
      if (e.key === "Escape") setOpenMenu(null);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, []);

  const timeText = useMemo(
    () =>
      new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
    []
  );

  return (
    <header
      ref={rootRef}
      className="mac-menuBar"
      role="banner"
      aria-label="Menu bar"
    >
      <div className="mac-menuLeft" role="menubar" aria-label="Application menu">
        <MenuItem>
          <span className="mac-apple">
            <img
              className="mac-menuLogo"
              src={codevexLogo}
              alt="CodeVex"
              draggable="false"
            />
          </span>
        </MenuItem>
        <MenuItem>
          <strong>CodeVex</strong>
        </MenuItem>

        <div className="mac-menuGroup">
          <MenuItem
            aria-haspopup="menu"
            isOpen={openMenu === "file"}
            onClick={() => setOpenMenu((m) => (m === "file" ? null : "file"))}
          >
            {hasOpenMemo ? "Memo" : "File"}
          </MenuItem>
          {openMenu === "file" ? (
            <div
              className="mac-menuDropdown"
              role="menu"
              aria-label={hasOpenMemo ? "Memo" : "File"}
            >
              <button
                type="button"
                className="mac-menuDropdownItem"
                role="menuitem"
                onClick={() => {
                  setOpenMenu(null);
                  onNewMemo?.();
                }}
              >
                New Memo…
              </button>

              {hasOpenMemo ? (
                <>
                  <div className="mac-menuDivider" role="separator" />
                  <button
                    type="button"
                    className="mac-menuDropdownItem"
                    role="menuitem"
                    disabled={!canDeleteMemo}
                    aria-disabled={!canDeleteMemo}
                    onClick={() => {
                      if (!canDeleteMemo) return;
                      setOpenMenu(null);
                      onDeleteMemo?.();
                    }}
                  >
                    Delete Memo…
                  </button>
                  <div className="mac-menuDivider" role="separator" />
                  <button
                    type="button"
                    className="mac-menuDropdownItem"
                    role="menuitem"
                    disabled={!hasDeletableMemos}
                    aria-disabled={!hasDeletableMemos}
                    onClick={() => {
                      if (!hasDeletableMemos) return;
                      setOpenMenu(null);
                      onDeleteAllMemos?.();
                    }}
                  >
                    Delete All Memos…
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mac-menuGroup">
          <MenuItem
            aria-haspopup="menu"
            isOpen={openMenu === "edit"}
            onClick={() => setOpenMenu((m) => (m === "edit" ? null : "edit"))}
          >
            Edit
          </MenuItem>
          {openMenu === "edit" ? (
            <div className="mac-menuDropdown" role="menu" aria-label="Edit">
              <button
                type="button"
                className="mac-menuDropdownItem"
                role="menuitem"
                disabled={!hasDeletableMemos}
                aria-disabled={!hasDeletableMemos}
                onClick={() => {
                  if (!hasDeletableMemos) return;
                  setOpenMenu(null);
                  onArrangeIcons?.();
                }}
              >
                Arrange
              </button>

              <div className="mac-menuDivider" role="separator" />
              <button
                type="button"
                className="mac-menuDropdownItem"
                role="menuitemcheckbox"
                aria-checked={Boolean(snapToGrid)}
                onClick={() => {
                  onToggleSnapToGrid?.();
                }}
              >
                <span className="mac-check">{snapToGrid ? "✓" : ""}</span>
                Snap to Grid
              </button>
            </div>
          ) : null}
        </div>

        <MenuItem>Help</MenuItem>
      </div>

      <div className="mac-menuRight" aria-label="Status area">
        <span className="mac-clock" title="Local time">
          {timeText}
        </span>
      </div>
    </header>
  );
}

